<script setup lang="ts">
import type { StringMap } from '@/types/common';
import dayjs from 'dayjs';
import { isObject } from 'lodash';

const LIMIT_SCROLL_HEIGHT_DESKTOP = 520; // Landing's page banner height(600) - header's height(80)
const LIMIT_SCROLL_HEIGHT_MOBILE = 420; // Landing's page banner height(500) - header's height(80)

const layoutStore = useLayoutStore();
const authStore = useAuthStore();
const { isMobile, isTablet } = useWindowResize();
const profileStore = useProfileStore();
const route = useRoute();

const currentYear = dayjs().year();

const isScrolling = ref(false);
const showConfirmSwitchUiDialog = ref(false);

const isJapanDiscoveryPage = computed(() => {
	return (
		route.name === ROUTE.VISIT_JAPAN.NAME || route.name === ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME
	);
});
const isAccessible = computed(() => layoutStore.isAccessible);
const isHomePage = computed(() => route.name === ROUTE.INDEX.NAME);
const isJapanDiscoveryPages = computed(
	() =>
		route.name === ROUTE.VISIT_JAPAN.NAME || route.name === ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
);
const profileCountry = computed(() => profileStore.profile?.countryId);

const isInformationPage = computed(() => {
	const pages = [ROUTE.INDEX.NAME, ROUTE.PRICING.NAME, ROUTE.ABOUT_US.NAME, ROUTE.VISIT_JAPAN.NAME];
	// Include all /feature pages as an information page
	const featurePages = ROUTE_NAMES.filter((route) => route.startsWith('feature-')).map(
		(route) => route,
	);
	return [...pages, ...featurePages].includes(route.name?.toString() || '');
});

const isHeaderTransparent = computed(() => isHomePage.value && !isScrolling.value);
const headerClass = computed(() => ({
	home: isHomePage.value,
	transparent: isHeaderTransparent.value,
	scrolling: isScrolling.value,
}));
const isShowFullScreen = computed(() => layoutStore.isFullScreen);

function handleScroll() {
	if (!window.top) {
		return;
	}

	const homePageLimitHeight = isMobile.value
		? LIMIT_SCROLL_HEIGHT_MOBILE
		: LIMIT_SCROLL_HEIGHT_DESKTOP;
	const defaultLimitHeight = 1;
	const limitHeight = isHomePage.value ? homePageLimitHeight : defaultLimitHeight;

	if (window.top.scrollY > limitHeight && !isScrolling.value) {
		isScrolling.value = true;
	} else if (window.top.scrollY < limitHeight && isScrolling.value) {
		isScrolling.value = false;
	}
}

function handleConfirmSwitchUI() {
	// Navigate to old peatix website without sign out
	authStore.proceedSecondSignIn();
}

function captureUtmParams() {
	const storedUtmValues = getSessionStorage(SESSION_STORAGE_ITEMS.UTM);
	const utmValues: StringMap = isObject(storedUtmValues) ? (storedUtmValues as any) : {};

	const newUtmValues: StringMap = {
		utm_source: getQueryParam('utm_source'),
		utm_medium: getQueryParam('utm_medium'),
		utm_campaign: getQueryParam('utm_campaign'),
		utm_term: getQueryParam('utm_term'),
	};

	// Check if there are any valid UTM values
	const hasValidUtmValues = Object.values(newUtmValues).some(
		(value) => value !== null && value !== '',
	);

	// Stop function execution if no valid UTM values
	if (!hasValidUtmValues) {
		return;
	}

	// Update the utmValues object with the new values, but only if the new value is not an empty string
	// This ensures that we preserve the previous UTM values as long as possible until the session ends or a new value is provided
	let isUtmUpdated = false;
	Object.keys(newUtmValues).forEach((key) => {
		if (newUtmValues[key]) {
			utmValues[key] = newUtmValues[key];
			isUtmUpdated = true;
		}
	});

	// Remove any empty values from the utmValues object
	Object.keys(utmValues).forEach((key) => {
		if (utmValues[key] === '') {
			delete utmValues[key];
		}
	});

	if (isUtmUpdated) {
		setSessionStorage(SESSION_STORAGE_ITEMS.UTM, JSON.stringify(utmValues));
	}
}

watch([profileCountry, isJapanDiscoveryPage], ([countryId, isJapanPage]) => {
	// Don't do anything until route.name is available
	if (!route.name) {
		showConfirmSwitchUiDialog.value = false;
		return;
	}

	if (!profileStore.profileId) {
		showConfirmSwitchUiDialog.value = false;
		return;
	}

	if (isJapanPage) {
		showConfirmSwitchUiDialog.value = false;
		return;
	}

	if (countryId && countryId !== DEFAULT_COUNTRY) {
		showConfirmSwitchUiDialog.value = true;
	} else if (countryId && countryId === DEFAULT_COUNTRY && authStore.isReadyForSecondSignIn) {
		authStore.proceedSecondSignIn();
		showConfirmSwitchUiDialog.value = false;
	} else {
		showConfirmSwitchUiDialog.value = false;
	}
});

onMounted(() => {
	window.addEventListener('scroll', handleScroll);

	// Capture UTM values and store them in session storage if there are any parameters in the URL
	if (new URL(window.location.href).searchParams.toString() !== '') {
		captureUtmParams();
	}
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
	<div v-if="isAccessible" :class="{ 'page-action-bar': layoutStore.hasActionBar }">
		<header v-if="!isShowFullScreen" :class="headerClass">
			<div class="search-container">
				<RouterLink :to="ROUTE.INDEX.PATH">
					<Logo :is-transparent-background="isHeaderTransparent" />
				</RouterLink>
				<EventSearchBox v-if="!isTablet && !isJapanDiscoveryPages" />
			</div>
			<div class="menu-container">
				<EventSearchBox
					v-if="!isJapanDiscoveryPages && isTablet"
					:is-transparent-background="isHeaderTransparent"
					is-tablet
				/>
				<Menu :is-transparent-background="isHeaderTransparent" />
			</div>
		</header>
		<main :class="{ information: isInformationPage }">
			<BaseNotificationBox />
			<RouterView />
		</main>
		<footer v-if="!isShowFullScreen">
			<FooterLink />
			<span class="copyright">© {{ currentYear }} Peatix</span>
		</footer>
		<RedirectDialog
			:is-show="showConfirmSwitchUiDialog"
			:is-japan-discovery-page="isJapanDiscoveryPage"
			@on-confirm="handleConfirmSwitchUI"
		/>
	</div>
</template>

<style lang="scss" scoped>
$headerHeight: rem(80);
$footerHeightDesktop: rem(120);
$footerHeightMobile: rem(288);

.page-action-bar {
	// Add margin bottom if page has action bar
	margin-bottom: rem(62);

	@include media-query-max(mobile) {
		margin-bottom: rem(102);
	}
}

header,
footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: spacings-get(2);
	box-sizing: border-box;
}

header {
	position: sticky;
	top: calc(env(safe-area-inset-top, 0) + 0);
	padding: spacings-get(3) spacings-get(8);
	height: $headerHeight;
	background-color: colors-get(base, white);
	z-index: 3;

	// Transition
	-webkit-transition: all 0.5s ease-out;
	-moz-transition: all 0.5s ease-out;
	-o-transition: all 0.5s ease-out;
	transition: all 0.5s ease-out;

	&.home {
		position: fixed;
		width: 100%;
	}

	&.transparent {
		background-color: transparent;
	}

	&.scrolling {
		position: sticky;
		top: 0;
		box-shadow: 0px 1px 1px 0px rgba(16, 24, 40, 0.05);
	}

	.search-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: spacings-get(6);
	}

	.menu-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: spacings-get(4);
	}
}

footer {
	padding: 0 spacings-get(8);
	height: $footerHeightDesktop;
}

main {
	min-height: calc(100dvh - ($headerHeight + $footerHeightDesktop)); // header + footer
	margin: auto;

	&:not(.information) {
		max-width: 1440px;
		padding: 0 spacings-get(4);

		@include media-query-max(mobile) {
			padding: 0;
		}
	}

	@include media-query-max(tablet) {
		min-height: calc(100dvh - ($headerHeight + $footerHeightMobile)); // header + footer
	}
}

footer {
	.copyright {
		@include fonts-get(semibold, text-md);
		color: colors-get(gray, 500);
	}
}

@include media-query-max(tablet) {
	header {
		padding: spacings-get(3) spacings-get(4);
	}

	footer {
		flex-direction: column;
		align-items: center;
		row-gap: rem(18);
		padding: spacings-get(12) 0;
		height: auto;
	}
}
</style>
